import React, { useState } from 'react';
import { useInView } from 'react-intersection-observer';
import './ServicesPortfolio.css';

const ServicesPortfolio = () => {
  const { ref, inView } = useInView({
    threshold: 0.1,
    triggerOnce: true
  });

  const [expandedService, setExpandedService] = useState(null);

  const toggleService = (index) => {
    setExpandedService(expandedService === index ? null : index);
  };

  const services = [
    {
      id: 1,
      icon: 'fas fa-shield-alt',
      title: 'Governance, Risk, and Compliance (GRC) Architecture',
      description: 'Develop, implement, and optimize integrated GRC frameworks to align organizational risk management, internal controls, and compliance programs with strategic business objectives and regulatory requirements. This service focuses on providing strategic counsel to leadership and the Audit Committee to foster a culture of integrity and accountability.',
      subServices: [
        {
          activity: 'Enterprise Risk Management (ERM) Program Design',
          detail: 'Define the Risk Appetite Statement and implement a standardized, cross-functional risk assessment methodology.',
          outcome: 'Strategic Clarity: Leadership gains a quantifiable understanding of acceptable risk, ensuring resource allocation and strategic decisions are fully informed and optimized for return.',
          deliverables: [
            'Risk Heat Map Dashboard: A visual representation (dashboard) plotting inherent and residual risks by likelihood and impact, showing current exposure vs. risk appetite.',
            'Risk Register: A comprehensive master document detailing inherent risk, control activities, and residual risk scores (Likelihood vs. Impact Matrix).'
          ]
        },
        {
          activity: 'Compliance Management & Regulatory Mapping',
          detail: 'Map all or related complex regulatory requirements (local and international) to existing policies and controls, identifying and remediating gaps proactively.',
          outcome: 'Reduced Regulatory Exposure: Proactive gap identification and remediation minimizes fines, penalties, and ensures adherence to external obligations.',
          deliverables: [
            'Integrated GRC Infographic: A visual summary of regulatory adherence, standards, and policy enforcement, perhaps using a Venn Diagram model to illustrate the alignment and dependencies between Governance, Risk, and Compliance.'
          ]
        },
        {
          activity: 'Governance Advisory',
          detail: 'Counsel the Board and Audit Committee on governance best practices, ethical culture, and optimizing the Three Lines Model (formerly known as the Three Lines of Defense).',
          outcome: 'Enhanced Accountability: Strengthens the structure for oversight, fostering a transparent and accountable culture from the Board level down.',
          deliverables: [
            'Board/Committee Charter Documentations: A standardized document detailing the committee\'s purpose, authority, composition, and responsibilities.'
          ]
        }
      ]
    },
    {
      id: 2,
      icon: 'fas fa-search-dollar',
      title: 'Internal Audit (IA) and Assurance Services',
      description: 'Leveraging the experiences in founding critical business functions like Internal Audit and professional qualification as CIA, this service establishes or transforms the IA function. It ensures the department is risk-based, technology-enabled (using tools like Arbutus, AutoAudit, etc.), that delivers high-value and objective assurance across the organization.',
      subServices: [
        {
          activity: 'IA Department Establishment (Startup) and Mentorship',
          detail: 'Develop the IA Charter, define the audit methodology, establish the Quality Assurance and Improvement Program (QAIP), and create the risk-based annual audit plan.',
          outcome: 'Credible Assurance: Rapid establishment of an IA function that immediately adheres to IIA Standards, providing the Audit Committee with reliable, independent assurance.',
          deliverables: [
            'Internal Audit Charter & Methodology: The foundational document for the IA department\'s authority, responsibility, and step-by-step procedures for conducting audits.'
          ]
        },
        {
          activity: 'Risk-Based Audit Program Development',
          detail: 'Involves designing a specific audit plan (program) for an engagement by prioritizing risk. Utilizing results from the ERM and GRC assessments, the program defines the scope, objectives, and detailed testing procedures (e.g., sample size, steps) to ensure Internal Audit (IA) resources are exclusively focused on the areas presenting the highest financial, operational, or compliance risk.',
          outcome: 'Optimized Internal Audit (IA) resources: Optimally deployed by integrating ERM/GRC assessments to define the scope, objectives, and detailed testing procedures for an audit engagement, thus adhering to CIA standards and ensuring the Audit Committee\'s focus remains on the highest strategic risks and most critical areas for proactive control mitigation.',
          deliverables: [
            'Risk-Based Audit Program: A CIA-compliant document detailing the scope and specific testing procedures for an audit based on prioritized high risks, and the Annual Audit Plan Summary, which provides the Audit Committee with a clear linkage between the organization\'s top strategic risks (ERM) and the scheduled audit projects for the year.'
          ]
        },
        {
          activity: 'Data-Driven Audit and Analytics',
          detail: 'Implement continuous auditing techniques using data analytics to audit 100% of transactions, identify anomalies, and uncover fraud patterns and control weaknesses.',
          outcome: 'Proactive Issue Detection: Shifts IA from a reactive, sample-based approach to continuous monitoring, maximizing the discovery of critical control gaps and hidden risks.',
          deliverables: [
            'Audit Findings Dashboard: A visualization tool tracking the status of open audit findings, remediation efforts, and management\'s adherence to agreed-upon deadlines.'
          ]
        },
        {
          activity: 'Control Penetration Testing',
          detail: 'Design and execute detailed audit programs to test the operational effectiveness and design of key controls (Financial, Operational, IT) through physical and system-based testing.',
          outcome: 'Validated Controls: Provides management with confidence that essential internal controls are operating as designed, validating the control environment\'s resilience.',
          deliverables: [
            'Audit Program Template: A customizable, step-by-step guide used by auditors to execute a specific audit (e.g., Cash Management Audit Program, ITAC, ITGC, etc.).'
          ]
        }
      ]
    },
    {
      id: 3,
      icon: 'fas fa-chart-line',
      title: 'Business Excellence and Control Standardization',
      description: 'Expert in structuring processes and leading a Business Excellence function centered on operational efficiency. This service focuses on eliminating waste, reducing variation, and ensuring processes are effective, sustainable, and compliant through process walkthrough, mapping, and policy standardization (IPMP expertise).',
      subServices: [
        {
          activity: 'End-to-End Process Mapping & Walkthroughs',
          detail: 'Conduct detailed process walkthroughs and mapping for core operations (e.g., Procure-to-Pay, Treasury), identifying bottlenecks, redundancy, and control gaps. (Process Re-design & Standardization)',
          outcome: 'Operational Efficiency: Streamlined processes and control simplification lead to lower operational costs, faster cycle times, and maximized efficiency.',
          deliverables: [
            'Process Flowcharts/Maps: Visual diagrams illustrating the current-state and future-state end-to-end flow of a business process, highlighting control points and responsible roles.'
          ]
        },
        {
          activity: 'Policy & Procedure Standardization (IPMP Expertise)',
          detail: 'Review and rationalize corporate policies across all legal entities and functions to ensure they are consistent, fit-for-purpose, and actively managed through a Policy Lifecycle Management (PLM) process.',
          outcome: 'Sustainable Operations: Creates a unified, easily maintainable, and highly compliant documentation framework that supports global consistency.',
          deliverables: [
            'Standard Operating Procedures (SOPs): Customized, purposely fit, detailed, step-by-step written guidelines and framework on how to perform a specific task or operation (e.g., Finance Manual, AML Policy, Whistle Blowing, Anti-Bribery, Vendor Onboarding SOP, etc.).'
          ]
        },
        {
          activity: 'Internal Control Framework Establishment',
          detail: 'Design a consistent framework that links process steps to control objectives, ownership, and key performance indicators (KPIs) for monitoring.',
          outcome: 'Clarity of Ownership: Ensures clear control ownership and accountability, critical for maintaining compliance and efficiency over time.',
          deliverables: [
            'Process Control Matrix: A table linking process steps, identified risks, control descriptions, control owners, frequency, and evidence of operation. (Code of Conduct, DOA, RACI Matrix, SOD, Access Rights, etc.)'
          ]
        },
        {
          activity: 'Training and Awareness Programs',
          detail: 'Embeds a culture of compliance. Designs and delivers customized training based on risk/audit findings. Includes managing Phishing Campaigns and penetration Testing to measure and strengthen employee resilience.',
          outcome: 'Measurable Compliance: Quantifies improvement via reduced phishing click rates and post-training scores. Culture of Integrity: Empowers employees as the First Line of Defense, directly reducing human error and the opportunity for fraud.',
          deliverables: [
            'Custom Training Modules: A combination of standard and custom fit contents for employee awareness on ethics, anti-fraud, and compliance policies. (Interactive, role-based content (video/SCORM))',
            'Phishing Campaign Report: Details employee susceptibility to attacks and provides recommendations for targeted awareness interventions.'
          ]
        }
      ]
    },
    {
      id: 4,
      icon: 'fas fa-user-shield',
      title: 'Internal Control and Anti-Fraud Control Management',
      description: 'Leveraging CPA, CFE, CISA, and CC credentials, this service focuses on the integrity of the control environment and active management of all fraud risks. It includes Internal Control, Anti-Fraud Control Management, and out-of-the-box improvements to build awareness and control maturity. To protect the company\'s financial assets and corporate reputation through robust financial controls, advanced fraud detection capabilities, and leadership of complex investigations.',
      subServices: [
        {
          activity: 'Anti-Fraud Control Management (CFE Expertise)',
          detail: 'Conduct a formal Fraud Risk Assessment and integrate preventative/detective anti-fraud controls into high-risk processes. Direct complex investigations to safeguard corporate assets.',
          outcome: 'Maximum Asset Protection: Implements controls designed by a Certified Fraud Examiner to actively deter, prevent, and quickly detect fraud schemes.',
          deliverables: [
            'Anti-Fraud Control Matrix: A table linking common fraud schemes (e.g., asset misappropriation, financial statement fraud, ghost vendors, etc.) to the specific controls designed to mitigate them.'
          ]
        },
        {
          activity: 'Investigations and Forensic Support',
          detail: 'CFE-led, confidential investigations (fraud, misconduct, policy violations). Activities: Initial issue triage, digital and physical evidence preservation, forensic interviewing, and objective reporting to leadership and legal counsel.',
          outcome: 'Financial Loss Minimization: Stops ongoing fraud swiftly, limiting financial damage and aiding in recovery. Defensible Findings: Ensures all findings are rigorous and support disciplinary or legal action.',
          deliverables: [
            'Investigation Report: A detailed report for documenting the investigation scope, findings, evidence, and recommendations for disciplinary and control actions.',
            'Forensic Evidence Log: Detailed log tracking the chain of custody for all collected evidence (physical and digital).'
          ]
        },
        {
          activity: 'Control Self-Assessment (CSA) Program',
          detail: 'Design and implement the CSA program and training to assess and report on the effectiveness of their own controls.',
          outcome: 'Proactive Risk Mitigation: Empowers process owners (1st line) to own their controls actively, leading to earlier identification and remediation of control weaknesses.',
          deliverables: [
            'Self-Assessment (CSA) Form/Template: A structured questionnaire/matrix (e.g., Control, Risk, and Compliance Matrix) to enable process owners to assess control effectiveness and identify improvement opportunities.'
          ]
        },
        {
          activity: 'Technical Control Assurance (CISA, CC) to evaluate IT Controls (ITGC, ITAC)',
          detail: 'Evaluates IT controls (ITGC and ITAC) to assure system integrity, data reliability, and cybersecurity posture. It links technical controls to financial and operational risk.',
          outcome: 'Cyber Resilience and Reduced System Risks: Protects critical information systems against unauthorized access, data breaches, and system failures, ensuring data and system integrity.',
          deliverables: [
            'IT General Controls (ITGC) Report: A formal report detailing test results, weaknesses, and risks for IT controls (e.g., Change Management, User Access).',
            'IT Application Control (ITAC) Report: A concise report confirming the design and operating effectiveness of automated controls (e.g., input validation, automated calculation) within a specific business application. It highlights control deficiencies, assesses associated risk, and provides actionable recommendations to ensure data integrity for CPA and financial reporting needs.'
          ]
        }
      ]
    }
  ];

  return (
    <section id="services-portfolio" className="services-portfolio-section" ref={ref}>
      <div className="container">
        {/* Header */}
        <div className={`portfolio-header ${inView ? 'fade-in' : ''}`}>
          <h2 className="section-title center">PORTFOLIO</h2>
          <div className="title-underline center"></div>
          <h3 className="portfolio-subtitle">A 360-Degree Portfolio for Organizational Resilience</h3>
        </div>

        {/* Introduction */}
        <div className={`portfolio-intro-section ${inView ? 'fade-in' : ''}`}>
          <div className="intro-card">
            <p className="intro-text">
              Welcome to this professional portfolio that represents the strategic convergence of expertise, not merely a collection of titles.
            </p>
            <p className="intro-text">
              In today's complex business environment, siloed assurance functions are no longer sufficient. You need a 
              leader who can not only perform an audit but also understand the financial implications, the regulatory 
              necessity, and the strategic opportunity behind every finding.
            </p>
            <p className="intro-text">
              This portfolio is the realization of two decades spent deliberately synthesizing the critical domains of 
              Business Operations, Finance, Internal Audit, Governance, Risk, and Compliance (GRC).
            </p>
            <div className="intro-highlight">
              <i className="fas fa-bullseye"></i>
              <div>
                <p>
                  We offer integrated solutions designed to move your organization beyond simple compliance and toward 
                  genuine business excellence. We don't just report on control failures; we design, implement, and embed 
                  the frameworks that prevent them, ensuring every strategic decision is grounded in financial acumen, 
                  robust assurance, and proactive risk management.
                </p>
                <p className="intro-tagline">
                  <strong>This is more than a service offering; it is the blueprint for building a resilient, ethical, 
                  and high-performing organization.</strong>
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Services Grid */}
        <div className="services-grid">
          {services.map((service, index) => (
            <div 
              key={service.id} 
              className={`service-card ${inView ? 'fade-in' : ''} ${expandedService === index ? 'expanded' : ''}`}
              style={{ animationDelay: `${index * 0.15}s` }}
            >
              <div className="service-header" onClick={() => toggleService(index)}>
                <div className="service-icon-section">
                  <div className="service-icon">
                    <i className={service.icon}></i>
                  </div>
                  <div className="service-number">{service.id}</div>
                </div>
                <div className="service-title-section">
                  <h3 className="service-title">{service.title}</h3>
                  <p className="service-description">{service.description}</p>
                </div>
                <button className="expand-btn" aria-label="Expand service">
                  <i className={`fas fa-chevron-${expandedService === index ? 'up' : 'down'}`}></i>
                </button>
              </div>

              {expandedService === index && (
                <div className="service-details">
                  {service.subServices.map((subService, subIndex) => (
                    <div key={subIndex} className="sub-service">
                      <div className="sub-service-header">
                        <i className="fas fa-caret-right"></i>
                        <h4>{subService.activity}</h4>
                      </div>
                      <p className="sub-service-detail">{subService.detail}</p>
                      
                      <div className="outcome-section">
                        <div className="outcome-label">
                          <i className="fas fa-check-circle"></i>
                          <strong>Outcome & Benefits</strong>
                        </div>
                        <p>{subService.outcome}</p>
                      </div>

                      <div className="deliverables-section">
                        <div className="deliverables-label">
                          <i className="fas fa-file-alt"></i>
                          <strong>Sample Deliverables & Artifacts</strong>
                        </div>
                        <ul>
                          {subService.deliverables.map((deliverable, dIndex) => (
                            <li key={dIndex}>{deliverable}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ServicesPortfolio;

